import * as superagent from 'superagent';
import {
    AbortableRequest,
    makePendingResponse,
    makeResponse as makeHttpResponse,
    Response,
} from 'dg-web-shared/lib/HttpResponse.ts';
import { Store, Update } from 'dg-web-shared/lib/Flux.tsx';
import * as AuthenticationState from './app/state/AuthenticationState.ts';

import { OPERATOR_EXTERNAL_ID_HEADER } from './utils/Utils.ts';

export type { Response } from 'dg-web-shared/lib/HttpResponse.ts';

export type RawRequest = superagent.Request;

export interface RequestWrite {
    (store: Store, res: Response): string;
}

let operatorExternalId: string | null = null;
export function setOperatorExternalId(value: string | null): void {
    operatorExternalId = value;
}

function isSameOriginRequest(req: RawRequest): boolean {
    return (
        new URL(req.url, window.location.origin).origin ===
        window.location.origin
    );
}

function requestMetadata(req: RawRequest): AbortableRequest {
    return {
        abort: () => {
            req.abort();
        },
        url: req.url,
        method: req.method,
        payload: '_data' in req ? req._data : undefined,
    };
}

export function makeResponse(
    err: unknown,
    req: RawRequest,
    sRes: superagent.Response | undefined,
): Response {
    const res = makeHttpResponse(
        err,
        requestMetadata(req),
        sRes === undefined
            ? undefined
            : {
                  status: sRes.status,
                  type: sRes.type,
                  text: sRes.text,
                  headers: sRes.headers,
                  body: sRes.body,
                  url: sRes.xhr?.responseURL,
                  readyState: sRes.xhr?.readyState,
              },
    );

    return res;
}

export function globalRequestBehaviorUpdate(store: Store, res: Response): void {
    const statusCode = res.statusCode;
    // avoid recursive import by using the key of the Auth State Slice...
    const auth: AuthenticationState.State = store.get(
        'app-AuthenticationState',
    );
    if (statusCode.unauthorized && auth.status.authorized) {
        window.location.reload();
    }
}

export function request<V>(
    reqGen: (args: V) => RawRequest,
    requestUpdate: RequestWrite,
): Update<V> {
    return (store: Store, args?: V): string => {
        const req = reqGen(args!);

        if (operatorExternalId && isSameOriginRequest(req)) {
            req.set(OPERATOR_EXTERNAL_ID_HEADER, operatorExternalId);
        }

        const pendingName =
            requestUpdate(store, makePendingResponse(requestMetadata(req))) +
            '-requestPending-log-ignore';
        req.end((err: unknown, superagentRes: superagent.Response) => {
            const res = makeResponse(err, req, superagentRes);
            globalRequestBehaviorUpdate(store, res);
            store.update(
                (store: Store, res?: Response) =>
                    requestUpdate(store, res!) + '-requestReturned-log-ignore',
                res,
            );
        });
        return pendingName;
    };
}
